import { useId, useState } from 'react';
import {
  buildBestInterestLanguage,
  buildTaxRefundCreditLanguage,
  normalizePlanLanguageAmount,
} from '../../utils/chapter13PlanLanguage';

const fieldClass = 'mt-1 block w-full rounded border bg-white px-2 py-1.5';
const buttonClass = 'rounded border bg-white px-3 py-1.5 text-xs hover:bg-gray-50 disabled:opacity-50';

export function TaxRefundTemplateControls({ onApply, initialAmount = '', disabled = false }) {
  const id = useId();
  const [amount, setAmount] = useState(initialAmount);
  const [error, setError] = useState('');
  const [applying, setApplying] = useState(false);
  const apply = async () => {
    if (applying || disabled) return;
    setError('');
    try {
      const text = buildTaxRefundCreditLanguage(amount);
      setApplying(true);
      await onApply(text);
    } catch (failure) {
      setError(failure.message || 'The tax refund template could not be applied.');
    } finally {
      setApplying(false);
    }
  };
  return <div className="space-y-2 rounded border p-3">
    <div className="text-xs font-semibold">Tax refund / CTC / EIC template</div>
    <label htmlFor={`${id}-retention`} className="block text-xs font-medium">Refund amount debtor may retain, before CTC and EIC</label>
    <input id={`${id}-retention`} type="text" inputMode="decimal" className={fieldClass} value={amount} onChange={event => setAmount(event.target.value)} disabled={disabled || applying} placeholder="0.00" />
    <button type="button" className={buttonClass} onClick={apply} disabled={disabled || applying}>{applying ? 'Applying…' : 'Use EIC / CTC retention template'}</button>
    {error && <p role="alert" className="text-xs text-red-700">{error}</p>}
  </div>;
}

export function BestInterestTemplateControls({
  allowedJoint = true,
  onApply,
  initialKind = 'non_joint',
  initialNonJointAmount = '',
  initialJointAmount = '',
  disabled = false,
}) {
  const id = useId();
  const [kind, setKind] = useState(['non_joint', 'joint_only', 'joint_and_non_joint'].includes(initialKind) ? initialKind : 'non_joint');
  const [nonJointAmount, setNonJointAmount] = useState(initialNonJointAmount);
  const [jointAmount, setJointAmount] = useState(initialJointAmount);
  const [error, setError] = useState('');
  const [applying, setApplying] = useState(false);
  const effectiveKind = allowedJoint ? kind : 'non_joint';
  const apply = async () => {
    if (applying || disabled) return;
    setError('');
    try {
      const result = buildBestInterestLanguage(effectiveKind, { jointAmount, nonJointAmount });
      const metadata = {
        kind: effectiveKind,
        jointAmount: effectiveKind !== 'non_joint' ? normalizePlanLanguageAmount(jointAmount) : null,
        nonJointAmount: effectiveKind !== 'joint_only' ? normalizePlanLanguageAmount(nonJointAmount) : null,
      };
      setApplying(true);
      await onApply(result, metadata);
    } catch (failure) {
      setError(failure.message || 'The best-interest template could not be applied.');
    } finally {
      setApplying(false);
    }
  };
  return <div className="space-y-3 rounded border p-3">
    <div className="text-xs font-semibold">Best-interest quick fill</div>
    <p className="text-xs text-gray-600">Joint templates concern joint debts and tenancy-by-the-entirety property, not whether both spouses filed bankruptcy. Select the applicable treatment.</p>
    <label htmlFor={`${id}-kind`} className="block text-xs font-medium">Best-interest template</label>
    <select id={`${id}-kind`} className={fieldClass} value={effectiveKind} onChange={event => setKind(event.target.value)} disabled={disabled || applying}>
      <option value="non_joint">Non-joint best interest</option>
      {allowedJoint && <option value="joint_only">Joint best interest only (TBE home)</option>}
      {allowedJoint && <option value="joint_and_non_joint">Joint and non-joint best interest (TBE home)</option>}
    </select>
    {effectiveKind !== 'non_joint' && <div>
      <label htmlFor={`${id}-joint`} className="block text-xs font-medium">Joint best-interest amount</label>
      <input id={`${id}-joint`} type="text" inputMode="decimal" className={fieldClass} value={jointAmount} onChange={event => setJointAmount(event.target.value)} disabled={disabled || applying} placeholder="0.00" />
    </div>}
    {effectiveKind !== 'joint_only' && <div>
      <label htmlFor={`${id}-nonjoint`} className="block text-xs font-medium">Non-joint best-interest amount</label>
      <input id={`${id}-nonjoint`} type="text" inputMode="decimal" className={fieldClass} value={nonJointAmount} onChange={event => setNonJointAmount(event.target.value)} disabled={disabled || applying} placeholder="0.00" />
    </div>}
    {effectiveKind !== 'non_joint' && <p className="text-xs text-gray-600">Use only when applicable to a tenancy-by-the-entirety marital home. Applying this template also adds the required notice in Section 8.1 without removing other provisions.</p>}
    <button type="button" className={buttonClass} onClick={apply} disabled={disabled || applying}>{applying ? 'Applying…' : 'Use best-interest template'}</button>
    {error && <p role="alert" className="text-xs text-red-700">{error}</p>}
  </div>;
}
